import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AuthService } from '@service/auth.service';
import { VersionsHistoryService } from '@service/versions-history.service';
import { PublishedVersion } from 'icreate/common/models/versions-history';
import { Vessel } from 'icreate/common/models/vessel';

@Component({
    selector: 'app-start-version-dialog',
    templateUrl: './start-version-dialog.component.html',
    styleUrls: ['./start-version-dialog.component.css'],
    standalone: false,
})
export class StartVersionDialogComponent implements OnInit {
    versionName: string;
    versions: PublishedVersion[];

    pageType: PageType = PageType.FIRST_PAGE;

    step: number;
    totalSteps: number;
    referenceVersion: string;
    isMajorUpdate: boolean;
    isTrialRun: boolean;

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            vessel: Vessel;
            shipyardIApi: string;
            inReferencePage: boolean;
            referenceVersion: string;
        },
        private authService: AuthService,
        private dialogRef: MatDialogRef<StartVersionDialogComponent>,
        private versionHistoryService: VersionsHistoryService,
        private router: Router
    ) {}

    async ngOnInit() {
        const shipyardId = this.data.shipyardIApi;
        const vesselId = this.data.vessel.iAPIVesselId;
        this.versions = await this.versionHistoryService.getVersionsList(shipyardId, vesselId);
        if (this.data.inReferencePage) this.pageType = PageType.VERSION_REFERENCE;
        if (this.data.referenceVersion) this.referenceVersion = this.data.referenceVersion;
        this.totalSteps = this.data.vessel.projectVersion ? 4 : 3;
    }

    isContinueDisabled() {
        if (this.isInFirstPage()) return false;
        else if (this.isInVersionReference()) return !this.referenceVersion;
        else if (this.isInUpdates()) return !(this.isMajorUpdate == true || this.isMajorUpdate == false);
        else if (this.isInTrialPage()) return !(this.isTrialRun == true || this.isTrialRun == false);
        return false;
    }

    isInFirstPage() {
        return this.pageType === PageType.FIRST_PAGE;
    }

    isInVersionReference() {
        return this.pageType === PageType.VERSION_REFERENCE;
    }

    isInUpdates() {
        return this.pageType === PageType.UPDATES;
    }

    isInTrialPage() {
        return this.pageType === PageType.TRIAL;
    }

    isInVersionName() {
        return this.pageType === PageType.VERSION_NAME;
    }

    getStepNumber(): number {
        if (this.isInVersionReference()) {
            return 1;
        }
        if (this.isInUpdates()) {
            if (this.totalSteps === 4) return 2;
            else return 1;
        }
        if (this.isInTrialPage()) {
            if (this.totalSteps === 4) return 3;
            else return 2;
        }
        if (this.isInVersionName()) {
            if (this.totalSteps === 4) return 4;
            else return 3;
        }
    }

    continue() {
        if (this.isInFirstPage()) {
            if (this.data.vessel.projectVersion) {
                this.pageType = PageType.VERSION_REFERENCE;
            } else {
                this.pageType = PageType.UPDATES;
            }
        } else if (this.isInVersionReference() && this.referenceVersion) {
            this.pageType = PageType.UPDATES;
        } else if (this.isInUpdates() && (this.isMajorUpdate == true || this.isMajorUpdate == false)) {
            this.pageType = PageType.TRIAL;
        } else if (this.isInTrialPage() && (this.isTrialRun == true || this.isTrialRun == false)) {
            this.pageType = PageType.VERSION_NAME;
        } else if (this.isInVersionName()) {
            this.startVersion();
        }
    }

    isStartDisabled(): boolean {
        if (this.isInVersionName()) {
            return !this.versionName;
        }
        return false;
    }

    startVersion() {
        if (this.isStartDisabled()) {
            return;
        }
        const currentUser = this.authService.getCurrentUser();
        this.dialogRef.close({
            referenceVersion: this.referenceVersion,
            versionName: this.versionName,
            owner: currentUser,
        });
    }

    clearForm() {
        this.dialogRef.close();
    }

    back() {
        if (this.isInVersionReference()) {
            this.pageType = PageType.FIRST_PAGE;
        } else if (this.isInUpdates()) {
            if (this.data.vessel.projectVersion) this.pageType = PageType.VERSION_REFERENCE;
            else this.pageType = PageType.FIRST_PAGE;
        } else if (this.isInTrialPage()) {
            this.pageType = PageType.UPDATES;
        } else if (this.isInVersionName()) {
            this.pageType = PageType.TRIAL;
        }
    }

    openVersionHistory() {
        const vessel = this.data.vessel;
        this.clearForm();
        this.router.navigateByUrl(`${vessel.shipyardId}/${vessel.id}/history`, {
            state: { origin: 'version-dialog' },
        });
    }

    referenceSelected(version) {
        this.referenceVersion = version;
    }
}

export enum PageType {
    undefined = 'undefined',
    FIRST_PAGE = 'first-page',
    VERSION_REFERENCE = 'version-reference',
    UPDATES = 'updates',
    TRIAL = 'trial',
    VERSION_NAME = 'version-name',
}
